import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Mail, ShieldCheck, ChevronDown, ChevronUp, Send, Key, CheckCircle, XCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/utils/api';
import { HolidaySettings } from '@/components/HolidaySettings';

const Settings = () => {
  const [components, setComponents] = useState([]);
  const [notifEmails, setNotifEmails] = useState([]);
  const [autoIncluded, setAutoIncluded] = useState([]);
  const [emailRules, setEmailRules] = useState([]);
  const [allRecipients, setAllRecipients] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [formData, setFormData] = useState({ name: '', type: 'Deduction', percentage: '', fixed_amount: '' });

  const [cleaning, setCleaning] = useState(false);
  const [taskEmails, setTaskEmails] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ recipients: [], subject: '', message: '' });
  const [sendingTask, setSendingTask] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [compRes, emailRes, recipientsRes, rulesRes, tasksRes] = await Promise.all([
        api.get('/salary-components'),
        api.get('/notification-emails'),
        api.get('/notification-emails/all-recipients'),
        api.get('/notification-rules'),
        api.get('/task-emails'),
      ]);
      setComponents(compRes.data);
      setNotifEmails(emailRes.data);
      setAutoIncluded(recipientsRes.data.auto_included || []);
      setEmailRules(rulesRes.data.events || []);
      setAllRecipients(rulesRes.data.all_recipients || []);
      setTaskEmails(tasksRes.data || []);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/salary-components', {
        name: formData.name,
        type: formData.type,
        percentage: formData.percentage ? parseFloat(formData.percentage) : null,
        fixed_amount: formData.fixed_amount ? parseFloat(formData.fixed_amount) : null,
      });
      toast.success('Salary component created');
      setDialogOpen(false);
      setFormData({ name: '', type: 'Deduction', percentage: '', fixed_amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await api.post('/notification-emails', { email: newEmail.trim(), label: newLabel.trim() });
      toast.success('Email added');
      setNewEmail('');
      setNewLabel('');
      setEmailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    }
  };

  const handleExcludeAutoEmail = async (email) => {
    try {
      await api.post('/notification-emails/exclude', { email });
      toast.success(`${email} removed from notifications`);
      fetchData();
    } catch { toast.error('Failed to exclude email'); }
  };

  const handleIncludeAutoEmail = async (email) => {
    try {
      await api.post('/notification-emails/include', { email });
      toast.success(`${email} re-added to notifications`);
      fetchData();
    } catch { toast.error('Failed to include email'); }
  };

  const handleToggleEventEnabled = async (event, currentEnabled) => {
    const rule = emailRules.find(r => r.event === event);
    try {
      await api.post('/notification-rules', { event, enabled: !currentEnabled, recipients: rule?.recipients || [] });
      toast.success(`Email ${!currentEnabled ? 'enabled' : 'disabled'} for this event`);
      fetchData();
    } catch { toast.error('Failed to update rule'); }
  };

  const handleToggleRecipient = async (event, email) => {
    const rule = emailRules.find(r => r.event === event);
    const currentRecipients = rule?.recipients || [];
    const newRecipients = currentRecipients.includes(email)
      ? currentRecipients.filter(e => e !== email)
      : [...currentRecipients, email];
    try {
      await api.post('/notification-rules', { event, enabled: rule?.enabled !== false, recipients: newRecipients });
      fetchData();
    } catch { toast.error('Failed to update recipients'); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (taskForm.recipients.length === 0) { toast.error('Select at least one recipient'); return; }
    try {
      await api.post('/task-emails', taskForm);
      toast.success('Task email created');
      setTaskDialogOpen(false);
      setTaskForm({ recipients: [], subject: '', message: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create task'); }
  };

  const handleSendTask = async (id) => {
    setSendingTask(id);
    try {
      await api.post(`/task-emails/${id}/send`);
      toast.success('Email sent successfully');
      fetchData();
    } catch { toast.error('Failed to send email'); }
    finally { setSendingTask(null); }
  };

  const handleDeleteTask = async (id) => {
    try {
      await api.delete(`/task-emails/${id}`);
      toast.success('Task deleted');
      fetchData();
    } catch { toast.error('Failed to delete task'); }
  };

  const handleTestEmail = async () => {
    const email = prompt('Enter email address to send test email:');
    if (!email) return;
    try {
      const res = await api.post('/notify/test-email', { email });
      if (res.data.success) {
        toast.success(`Test email sent to ${email}. Check inbox.`);
      } else {
        toast.error(`Failed: ${res.data.error}`);
      }
    } catch (err) { toast.error('Test email failed'); }
  };

  const toggleTaskRecipient = (email) => {
    setTaskForm(prev => ({
      ...prev,
      recipients: prev.recipients.includes(email)
        ? prev.recipients.filter(e => e !== email)
        : [...prev.recipients, email]
    }));
  };

  const handleDeleteEmail = async (id) => {
    try {
      await api.delete(`/notification-emails/${id}`);
      toast.success('Email removed');
      fetchData();
    } catch { toast.error('Failed to remove email'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  const deductions = components.filter(c => c.type === 'Deduction');
  const allowances = components.filter(c => c.type === 'Allowance');

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Settings</h1>
        <p className="mt-2 text-base text-slate-600">Configure email notifications and salary components</p>
      </div>

      {/* Data Maintenance Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="data-maintenance-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Data Maintenance</h2>
              <p className="text-sm text-slate-500">Remove duplicate salary records from the database</p>
            </div>
          </div>
          <Button variant="outline" size="sm" disabled={cleaning} onClick={async () => {
            setCleaning(true);
            try {
              const res = await api.post('/admin/cleanup-duplicate-salaries');
              toast.success(res.data.message);
            } catch { toast.error('Cleanup failed'); }
            finally { setCleaning(false); }
          }} data-testid="cleanup-duplicates-button">
            {cleaning ? 'Cleaning...' : 'Cleanup Duplicates'}
          </Button>
        </div>
      </div>

      <HolidaySettings />

      {/* Email Notifications Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="email-settings-section">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <Mail className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Email Notifications</h2>
              <p className="text-sm text-slate-500">Manage recipients for automated workflow emails</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleTestEmail} data-testid="test-email-btn">
              <Send className="h-4 w-4 mr-1.5" /> Test Email
            </Button>
            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-email-button">
                <Plus className="h-4 w-4 mr-1.5" />
                Add Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Notification Email</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddEmail} className="space-y-4">
                <div>
                  <Label htmlFor="email-to">Email Address</Label>
                  <Input id="email-to" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="e.g., john@company.com" required data-testid="email-to-input" />
                </div>
                <div>
                  <Label htmlFor="email-label">Label (optional)</Label>
                  <Input id="email-label" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g., Finance Head" data-testid="email-label-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="save-email-button">Add Email</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-xs font-medium text-slate-500 mb-0.5">Email From</p>
          <p className="text-sm font-semibold text-slate-800" data-testid="email-from-display">ashish@mrchows.co.in</p>
        </div>

        <div className="mb-4">
          <p className="text-xs font-medium text-slate-500 mb-2">All Email Recipients</p>
          
          {/* Auto-included Manager/CEO emails */}
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider mb-1.5">Auto-included (Role-based)</p>
            <div className="space-y-2">
              {autoIncluded.map((u, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${u.excluded ? 'bg-slate-100 border border-slate-200 opacity-60' : 'bg-blue-50 border border-blue-200'}`} data-testid={`auto-email-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${u.excluded ? 'bg-slate-200 text-slate-500' : 'bg-blue-100 text-blue-700'}`}>
                      {u.role === 'CEO' ? 'C' : 'M'}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${u.excluded ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{u.email}</p>
                      <p className="text-xs text-slate-500">{u.name} &middot; {u.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.excluded ? (
                      <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8 text-xs" onClick={() => handleIncludeAutoEmail(u.email)} data-testid={`include-auto-${i}`}>
                        Re-add
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleExcludeAutoEmail(u.email)} data-testid={`exclude-auto-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {autoIncluded.length === 0 && (
                <p className="text-sm text-slate-400 py-2 text-center">No Manager/CEO accounts found.</p>
              )}
            </div>
          </div>

          {/* Manually added emails */}
          <div>
            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-1.5">Manually Added</p>
            {notifEmails.length === 0 ? (
              <p className="text-sm text-slate-400 py-2 text-center">No additional recipients added yet.</p>
            ) : (
              <div className="space-y-2">
                {notifEmails.map(ne => (
                  <div key={ne.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`notif-email-${ne.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">+</div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{ne.email}</p>
                        {ne.label && <p className="text-xs text-slate-500">{ne.label}</p>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteEmail(ne.id)} data-testid={`delete-email-${ne.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <p className="text-xs font-medium text-slate-500 mb-3">Email Rules — Choose who receives each notification</p>
          <div className="space-y-2">
            {emailRules.map((rule) => {
              const isExpanded = expandedEvent === rule.event;
              const dotColor = rule.event === 'hr_submitted' ? 'bg-blue-500' : rule.event === 'manager_approved' ? 'bg-amber-500' : rule.event === 'for_directors' ? 'bg-purple-500' : 'bg-emerald-500';
              return (
                <div key={rule.event} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`rule-${rule.event}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => setExpandedEvent(isExpanded ? null : rule.event)}>
                    <div className="flex items-center gap-3">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`}></span>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{rule.label}</p>
                        <p className="text-[11px] text-slate-500">{rule.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={rule.enabled !== false}
                          onCheckedChange={() => handleToggleEventEnabled(rule.event, rule.enabled !== false)}
                          data-testid={`toggle-${rule.event}`}
                        />
                        <span className="text-[10px] text-slate-500">{rule.enabled !== false ? 'On' : 'Off'}</span>
                      </div>
                      <span className="text-xs text-slate-400 font-mono">{(rule.recipients || []).length} recipients</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 py-3 border-t border-slate-200 bg-white">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Select recipients for this event</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {allRecipients.map((r, i) => {
                          const isChecked = (rule.recipients || []).includes(r.email);
                          return (
                            <label key={i} className={`flex items-center gap-2.5 p-2 rounded-md cursor-pointer transition-colors ${isChecked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`}>
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => handleToggleRecipient(rule.event, r.email)}
                                data-testid={`recipient-${rule.event}-${i}`}
                              />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-slate-800 truncate">{r.email}</p>
                                <p className="text-[10px] text-slate-500">{r.name}{r.name && r.role ? ' \u00b7 ' : ''}{r.role}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {allRecipients.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No recipients available. Add emails above.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Task Emails Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="task-emails-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-50 rounded-lg">
              <Send className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Task Emails</h2>
              <p className="text-sm text-slate-500">Send custom reminder or task emails to anyone</p>
            </div>
          </div>
          <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="create-task-email-btn">
                <Plus className="h-4 w-4 mr-1.5" />
                New Task Email
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Task Email</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div>
                  <Label>Recipients</Label>
                  <div className="mt-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                    {allRecipients.map((r, i) => {
                      const isChecked = taskForm.recipients.includes(r.email);
                      return (
                        <label key={i} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer ${isChecked ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                          <Checkbox checked={isChecked} onCheckedChange={() => toggleTaskRecipient(r.email)} data-testid={`task-recip-${i}`} />
                          <span className="text-xs text-slate-800">{r.email}</span>
                          <span className="text-[10px] text-slate-400">{r.role}</span>
                        </label>
                      );
                    })}
                  </div>
                  {taskForm.recipients.length > 0 && (
                    <p className="text-[10px] text-indigo-600 mt-1">{taskForm.recipients.length} selected</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="task-subject">Subject</Label>
                  <Input id="task-subject" value={taskForm.subject} onChange={e => setTaskForm({...taskForm, subject: e.target.value})} placeholder="e.g., Complete attendance for March" required data-testid="task-subject-input" />
                </div>
                <div>
                  <Label htmlFor="task-message">Message</Label>
                  <textarea id="task-message" value={taskForm.message} onChange={e => setTaskForm({...taskForm, message: e.target.value})} placeholder="Write your message here..." required rows={4}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="task-message-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="create-task-submit">Create Task Email</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {taskEmails.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No task emails created yet.</p>
        ) : (
          <div className="space-y-2">
            {taskEmails.map(task => (
              <div key={task.id} className={`p-3 rounded-lg border ${task.status === 'sent' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`task-${task.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{task.subject}</p>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${task.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                        {task.status === 'sent' ? 'Sent' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2">{task.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">To: {(task.recipients || []).join(', ')}</p>
                    {task.sent_at && <p className="text-[10px] text-emerald-600 mt-0.5">Sent: {new Date(task.sent_at).toLocaleString('en-IN')}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {task.status !== 'sent' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        disabled={sendingTask === task.id} onClick={() => handleSendTask(task.id)} data-testid={`send-task-${task.id}`}>
                        <Send className="h-3 w-3 mr-1" />
                        {sendingTask === task.id ? 'Sending...' : 'Send'}
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteTask(task.id)} data-testid={`delete-task-${task.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Salary Components Section */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Salary Components</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setFormData({ name: '', type: 'Deduction', percentage: '', fixed_amount: '' });
        }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid="add-component-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Component
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Salary Component</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Component Name</Label>
                <Input id="name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., House Rent Allowance" required data-testid="component-name-input" />
              </div>
              <div>
                <Label htmlFor="type">Type</Label>
                <select id="type" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600" required data-testid="component-type-select">
                  <option value="Deduction">Deduction</option>
                  <option value="Allowance">Allowance</option>
                </select>
              </div>
              <div>
                <Label htmlFor="percentage">Percentage (%)</Label>
                <Input id="percentage" type="number" step="0.01" value={formData.percentage} onChange={e => setFormData({ ...formData, percentage: e.target.value })} placeholder="Optional" data-testid="percentage-input" />
              </div>
              <div>
                <Label htmlFor="fixed_amount">Fixed Amount</Label>
                <Input id="fixed_amount" type="number" step="0.01" value={formData.fixed_amount} onChange={e => setFormData({ ...formData, fixed_amount: e.target.value })} placeholder="Optional" data-testid="fixed-amount-input" />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-component-button">Create Component</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Deductions</h3>
          {deductions.length === 0 ? (
            <p className="text-slate-500 text-sm">No deduction components configured.</p>
          ) : (
            <div className="space-y-3">
              {deductions.map(component => (
                <div key={component.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`deduction-${component.id}`}>
                  <p className="font-semibold text-slate-900">{component.name}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    {component.percentage && `${component.percentage}%`}
                    {component.fixed_amount && `₹${component.fixed_amount}`}
                    {!component.percentage && !component.fixed_amount && 'Custom'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Allowances</h3>
          {allowances.length === 0 ? (
            <p className="text-slate-500 text-sm">No allowance components configured.</p>
          ) : (
            <div className="space-y-3">
              {allowances.map(component => (
                <div key={component.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`allowance-${component.id}`}>
                  <p className="font-semibold text-slate-900">{component.name}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    {component.percentage && `${component.percentage}%`}
                    {component.fixed_amount && `₹${component.fixed_amount}`}
                    {!component.percentage && !component.fixed_amount && 'Custom'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── ShiftPro Credentials ── */}
      <ShiftProCredentials />
    </div>
  );
};

/* ─── ShiftPro Credentials Sub-component ─── */
const ShiftProCredentials = () => {
  const [creds, setCreds] = useState({ default_email: '', default_password: '', roles: { HR: { email: '', password: '' }, Manager: { email: '', password: '' }, CEO: { email: '', password: '' } } });
  const [testResults, setTestResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => { fetchCreds(); }, []);

  const fetchCreds = async () => {
    try {
      const res = await api.get('/shiftpro/credentials');
      const d = res.data;
      setCreds({
        default_email: d.default_email || '',
        default_password: '',
        roles: {
          HR: { email: d.roles?.HR?.email || '', password: '' },
          Manager: { email: d.roles?.Manager?.email || '', password: '' },
          CEO: { email: d.roles?.CEO?.email || '', password: '' },
        }
      });
    } catch { /* first time, no saved creds */ }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { default_email: creds.default_email, default_password: creds.default_password, roles: {} };
      ['HR', 'Manager', 'CEO'].forEach(role => {
        const r = creds.roles[role];
        if (r.email && r.password) payload.roles[role] = { email: r.email, password: r.password };
      });
      const res = await api.post('/shiftpro/credentials', payload);
      const tokenReady = res.data?.token_ready;
      if (tokenReady) {
        toast.success('ShiftPro credentials saved & synced! Data will load now.');
      } else {
        toast.success('Credentials saved but token warm-up pending. Click Force Sync if data doesn\'t load.');
      }
      setTestResults(null);
      fetchCreds();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save credentials');
    } finally { setSaving(false); }
  };

  const handleForceSync = async () => {
    setTesting(true);
    try {
      const res = await api.post('/shiftpro/force-sync');
      toast.success(`Re-connected! ${res.data.users_fetched} ShiftPro users fetched.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Force sync failed — check credentials');
    } finally { setTesting(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post('/shiftpro/credentials/test');
      setTestResults(res.data);
      const allOk = Object.values(res.data).every(r => r.ok);
      if (allOk) toast.success('All ShiftPro logins working!');
      else toast.error('Some ShiftPro logins failed — check results below');
    } catch { toast.error('Could not test credentials'); }
    finally { setTesting(false); }
  };

  const togglePw = (key) => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));

  const PasswordInput = ({ id, value, onChange, placeholder }) => (
    <div className="relative">
      <Input type={showPasswords[id] ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder}
        className="pr-10 text-sm" data-testid={`sp-cred-${id}`} />
      <button type="button" onClick={() => togglePw(id)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {showPasswords[id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="shiftpro-credentials-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-100">
            <Key className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">ShiftPro Credentials</h3>
            <p className="text-xs text-slate-500">Update passwords here when you change them in ShiftPro</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleForceSync} disabled={testing} data-testid="force-sync-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${testing ? 'animate-spin' : ''}`} />
            Force Sync
          </Button>
          <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="test-sp-creds-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${testing ? 'animate-spin' : ''}`} />
            {testing ? 'Testing…' : 'Test Connection'}
          </Button>
        </div>
      </div>

      {testResults && (
        <div className="mb-5 p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-1.5" data-testid="sp-test-results">
          {Object.entries(testResults).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              {val.ok ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
              <span className="font-medium text-slate-700">{key}:</span>
              <span className="text-slate-500">{val.email}</span>
              <span className={val.ok ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>{val.ok ? 'Connected' : `Failed (${val.status || val.error})`}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <div className="p-4 rounded-lg border border-indigo-100 bg-indigo-50/30">
          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-3">Default (HR) Account</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-600">Email</Label><Input value={creds.default_email} onChange={e => setCreds(p => ({ ...p, default_email: e.target.value }))} placeholder="hr@mrchows.co.in" className="text-sm" data-testid="sp-cred-default-email" /></div>
            <div><Label className="text-xs text-slate-600">New Password</Label><PasswordInput id="default-pw" value={creds.default_password} onChange={e => setCreds(p => ({ ...p, default_password: e.target.value }))} placeholder="Enter new password" /></div>
          </div>
        </div>

        {['HR', 'Manager', 'CEO'].map(role => (
          <div key={role} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">{role} Role</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Email</Label><Input value={creds.roles[role].email} onChange={e => setCreds(p => ({ ...p, roles: { ...p.roles, [role]: { ...p.roles[role], email: e.target.value } } }))} placeholder={`${role.toLowerCase()}@mrchows.co.in`} className="text-sm" data-testid={`sp-cred-${role.toLowerCase()}-email`} /></div>
              <div><Label className="text-xs text-slate-600">New Password</Label><PasswordInput id={`${role.toLowerCase()}-pw`} value={creds.roles[role].password} onChange={e => setCreds(p => ({ ...p, roles: { ...p.roles, [role]: { ...p.roles[role], password: e.target.value } } }))} placeholder="Enter new password" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-5">
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-sp-creds-btn">
          {saving ? 'Verifying & Saving…' : 'Save Credentials'}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
